import {
  type BackendChoice,
  BackendChoiceSchema,
} from '@electron/shared/backend-choice.types';
import { apiClient } from '@littlebird/auth-client';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { userStore } from '@/stores/user.store';

const BACKEND_CHOICE_QUERY_KEY = ['backend-choice'];
const OPEN_WORK_QUERY_KEY = ['backend-choice', 'open-work'];

const BackendChoiceBodySchema = z.object({
  backend_choice: BackendChoiceSchema,
});

/** The server's answer, and the backend that served it when the server says so */
export interface BackendChoiceAnswer {
  backendChoice: BackendChoice;
  servedBy: string | null;
}

class BackendChoiceRequestError extends Error {
  constructor(readonly status: number) {
    super(`Backend choice request failed with HTTP ${status}`);
    this.name = 'BackendChoiceRequestError';
  }
}

// The raw response carries `x-lb-served-by`, which the parsed helpers drop
const requestBackendChoice = async (): Promise<BackendChoiceAnswer> => {
  const response = await apiClient.fetch('backend_choice');
  if (!response.ok) throw new BackendChoiceRequestError(response.status);
  const body = BackendChoiceBodySchema.parse(await response.json());
  return {
    backendChoice: body.backend_choice,
    servedBy: response.headers.get('x-lb-served-by'),
  };
};

export const useBackendChoiceQuery = () =>
  useQuery({
    queryKey: BACKEND_CHOICE_QUERY_KEY,
    queryFn: async () => {
      const generation = userStore.backendChoiceGenerationNow();
      const answer = await requestBackendChoice();
      // The server's answer is newer than the cached profile, but not than a
      // choice applied while it was out
      userStore.applyBackendChoice(answer.backendChoice, generation);
      return answer;
    },
    retry: false,
  });

// A cluster this build does not know keeps its name, so the work on the
// clusters it does know still shows
const ClusterNameSchema = z.string();

// `GET /backend_choice/open_work`: the user's open work on the backend they
// are not on. A cluster that cannot list the user's runs says why. A listing
// this build does not know reads as not configured, which shows nothing
const ClusterListingSchema = z.union([
  z.object({
    cluster: ClusterNameSchema,
    listing: z.literal('listed'),
    workflows: z.array(
      z.object({
        workflow_id: z.string(),
        workflow_type: z.string(),
        started_at: z.string().nullable(),
      })
    ),
  }),
  z.object({
    cluster: ClusterNameSchema,
    listing: z
      .enum(['search_attribute_off', 'not_configured', 'unreachable'])
      .catch('not_configured'),
  }),
]);

const OpenWorkElsewhereSchema = z.object({
  other_backend: BackendChoiceSchema,
  clusters: z.array(ClusterListingSchema),
  turns: z.array(
    z.object({
      turn_id: z.string(),
      chat_id: z.string(),
      chat_title: z.string().nullable(),
      state: z.string(),
    })
  ),
  chats: z.array(
    z.object({ chat_id: z.string(), chat_title: z.string().nullable() })
  ),
});

export type OpenWorkElsewhere = z.infer<typeof OpenWorkElsewhereSchema>;

/** Fetched when the Backend setting opens, and again when the choice changes. A failure shows nothing */
export const useOpenWorkElsewhereQuery = () =>
  useQuery({
    // Keyed by the choice, so a read still out when the choice changes is not
    // reused: a refetch of a query with no answer yet joins the read in flight
    queryKey: [...OPEN_WORK_QUERY_KEY, userStore.backendChoice],
    queryFn: async ({ signal }): Promise<OpenWorkElsewhere> => {
      // A 503 means the server cannot tell, which a retry does not change.
      // The signal cancels a read whose choice changed before it answered, so
      // going back to that choice reads afresh
      const response = await apiClient.fetch('backend_choice/open_work', {
        retryOptions: { retries: 0 },
        signal,
      });
      if (!response.ok) throw new BackendChoiceRequestError(response.status);
      return OpenWorkElsewhereSchema.parse(await response.json());
    },
    retry: false,
  });
